import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Access-token storage — mirrors the httpOnly cookie the backend also sets.
// Belt-and-suspenders: cookies can be blocked by browser policy, subdomain
// mismatches, or proxy rewrites in production. Attaching an Authorization
// header directly means auth still works even when cookies don't.
const TOKEN_KEY = "hs_access_token";

export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore quota / disabled storage */ }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach the Bearer token (if we have one) to every outbound request. The
// backend `get_current_user` dependency reads `Authorization: Bearer …` as a
// fallback when the cookie is missing, so this keeps auth flowing when the
// cookie layer fails.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Clear the stored token on any 401 so the app cleanly falls back to /login
// instead of retrying a dead token indefinitely.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) setAccessToken(null);
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Date helpers — all work with YYYY-MM-DD strings interpreted as LOCAL dates
// (so they don't shift across timezones). The pitfall to avoid: `new Date("2026-05-04")`
// parses as UTC midnight, which becomes "May 3" in any timezone west of UTC.

/** Parse "YYYY-MM-DD" as a local Date at noon (avoids DST edge cases). */
export function parseLocalDate(isoDate) {
  if (isoDate instanceof Date) return isoDate;
  const [y, m, d] = String(isoDate).split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Format a Date or "YYYY-MM-DD" as a local "YYYY-MM-DD" string. */
export function toLocalISODate(d) {
  const dt = d instanceof Date ? d : parseLocalDate(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Monday of the ISO week containing `dateStr` (default: today, in user's local timezone). */
export function mondayOf(dateStr) {
  const d = dateStr ? parseLocalDate(dateStr) : new Date();
  d.setHours(12, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = (day === 0 ? -6 : 1 - day); // shift to Monday
  d.setDate(d.getDate() + diff);
  return toLocalISODate(d);
}

/** Add `n` days to a "YYYY-MM-DD" string, returning a "YYYY-MM-DD" string in local time. */
export function addDays(isoDate, n) {
  const d = parseLocalDate(isoDate);
  d.setDate(d.getDate() + n);
  return toLocalISODate(d);
}

/** Today's date as "YYYY-MM-DD" in the user's local timezone. */
export function todayISO() {
  return toLocalISODate(new Date());
}

/** Format a "YYYY-MM-DD" or ISO timestamp for display, never shifting the day. */
export function formatDate(value, opts = { month: "short", day: "numeric", year: "numeric" }) {
  if (!value) return "";
  // Date-only strings → parse in local time
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseLocalDate(value).toLocaleDateString(undefined, opts);
  }
  // Full ISO timestamps → trust the time zone in the string
  return new Date(value).toLocaleDateString(undefined, opts);
}

export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Auto-format a duration in seconds for display.
 *   • < 60s          → "45s"
 *   • 60 – 3599s     → "M:SS" (e.g. "5:00", "1:30")
 *   • ≥ 3600s        → "Hh MM" (e.g. "1h 05")
 * Returns "" for falsy input.
 */
export function formatDuration(sec) {
  if (sec == null || sec === "" || Number.isNaN(Number(sec))) return "";
  const s = Math.max(0, Math.round(Number(sec)));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = String(s % 60).padStart(2, "0");
    return `${m}:${r}`;
  }
  const h = Math.floor(s / 3600);
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  return `${h}h ${m}`;
}

